import React, { PureComponent } from 'react';
import BaseMap from './BaseMap';
import { Grid, Typography, Slider } from '@mui/material';
import { debounce } from 'lodash';
import SchedulingLayer from './layers/SchedulingLayer';
import LiveRobotLayer from './layers/LiveRobotLayer';
import AssignmentButton from './buttons/AssignmentButton';
import DeselectAllButton from './buttons/DeselectAllButton';
import Collection from 'ol/Collection';
import { convertProjection4329, dedupeBy, generateScheduledKey, someObjectValuesChanged } from '../../utils';
import { getJobFromFeature } from '../../dataModelHelpers';
import { getCurrentShift, getShiftRecordById } from '../scheduling/helpers/shift';
import { alertError, alertInfo, alertWarn } from '../../alertDispatcher';
import { LocalAtRecord, Job } from '../../db';
import {
  ScheduleMap,
  JobBoundsMap,
  SchedulingBaseProps,
  KMLFeature,
  DefaultKMLFeatureProperties,
  SchedulingRecordTypes,
} from '../../types/types';
import Feature, { FeatureLike } from 'ol/Feature';
import GeometryType from 'ol/geom/GeometryType';
import SideBarInfoFields, { GroupStatisticsBy } from '../scheduling/SideBarInfoFields';
import { IoIosStats } from 'react-icons/io';
import MapButton from './buttons/MapButton';
import { createLocalAtRecordByAtModel, syncMulti } from '../../db_ops/airtable_record_ops';
import EventBus from '../../EventBus';
import * as Sentry from '@sentry/react';
import logger from '../../logger';
import { Geometry } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { JobsFieldSet, ShiftDropoff } from '@rogoag/airtable/crm';
import { dispatchSetCenter, dispatchSetZoom } from '../../mapEvents';
import LiveTruckLayer from './layers/LiveTruckLayer';
import { HeatStatsLayer, HeatmapAttribute } from './layers/HeatStatsLayer';
import { HeatmapHistogram } from './layers/HeatmapHistogram';
import { HeatmapAttributeCalculator } from './layers/domain/HeatmapAttribute';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { MdShowChart } from 'react-icons/md';
import { getLocalAtSearch } from '../../db_ops/airtable_search_ops';
import { Equipment } from '@rogoag/airtable/equipment';

export type StatisticsGrouped = Record<
  string,
  {
    totalFields: number;
    totalAcres: number;
    totalSamples: number;
    fieldsWithValidAcresNumber: number;
    fieldsWithValidSamplesNumber: number;
  }
>;

interface SchedulingMapProps {
  showJobs: boolean;
  showDropoffs: boolean;
  showRobots: boolean;
  showTrucks: boolean;
  filtered: LocalAtRecord[];
  jobBounds: JobBoundsMap;
  selectedShift: string;
  availableShifts: LocalAtRecord[];
  hiddenScheduled: string[];
  unassigned: LocalAtRecord<any>[];
  scheduled: ScheduleMap;
  zoomField?: LocalAtRecord;
  allRobots: LocalAtRecord[];
  allTrucks: Equipment[];
  robotRefreshRate: number;
  updateUnassigned: (unassigned: LocalAtRecord[]) => void;
  updateScheduled: (scheduled: ScheduleMap) => void;
  updateLoading: (loading: boolean) => void;
}

interface SchedulingMapState {
  selectedFeatures: Collection<Feature>;
  scheduleFeatures: KMLFeature<DefaultKMLFeatureProperties>[];
  showStats: boolean;
  totalAcres: number;
  totalOpsAcres: number;
  totalSamples: number;
  averageSamples: number;
  averageAcres: number;
  totalFields: number;
  jobLinks: { name: string; link: string }[];
  statsDrawActive: boolean;
  fieldBoundaries: FeatureLike[];
  statisticsGrouped: StatisticsGrouped;
  showHeatmap: boolean;
  heatmapAttribute: HeatmapAttribute;
  heatmapBlur: number;
  heatmapRadius: number;
  heatmapGradient: string[];
}

export default class SchedulingMap extends PureComponent<SchedulingMapProps, SchedulingMapState> {
  constructor(props: SchedulingMapProps) {
    super(props);

    this.state = {
      selectedFeatures: new Collection([]),
      scheduleFeatures: [],
      showStats: false,
      totalAcres: 0,
      totalOpsAcres: 0,
      totalSamples: 0,
      averageSamples: 0,
      averageAcres: 0,
      totalFields: 0,
      jobLinks: [],
      statsDrawActive: false,
      fieldBoundaries: [],
      statisticsGrouped: {},
      showHeatmap: false,
      heatmapAttribute: 'daysLate',
      heatmapBlur: 0,
      heatmapRadius: 20,
      heatmapGradient: ['#00f', '#0ff', '#0f0', '#ff0', '#f00'],
    };

    this.updateSelectedFeatures = this.updateSelectedFeatures.bind(this);
    this.assignShift = this.assignShift.bind(this);
    this.deselectAll = this.deselectAll.bind(this);
    this.prepareOrderedRecords = this.prepareOrderedRecords.bind(this);
    this.refreshJobRecords = this.refreshJobRecords.bind(this);
    this.findBlockerJobs = this.findBlockerJobs.bind(this);
    this.refreshAndValidateRecordsToBeScheduled = this.refreshAndValidateRecordsToBeScheduled.bind(this);
    this.setHeatmapBlur = this.setHeatmapBlur.bind(this);
    this.setHeatmapRadius = this.setHeatmapRadius.bind(this);
  }

  async componentDidMount() {
    await this.loadAllFeatures();
  }

  async componentDidUpdate(prevProps: SchedulingMapProps) {
    // this is a map with shift identifiers as keys and arrays of jobs as values
    // we want to reduce the array to just be the job IDs
    const scheduled: Record<string, string[]> = {};
    Object.keys(this.props.scheduled).forEach((key) => {
      scheduled[key] = this.props.scheduled[key].map((job) => job.id);
    });

    if (JSON.stringify(this.props.jobBounds) !== JSON.stringify(prevProps.jobBounds)) {
      if (this.props.showJobs) {
        await this.loadAllFeatures();
      }
    }

    if (
      someObjectValuesChanged(this.props, prevProps, [
        'showJobs',
        'showDropoffs',
        'showRobots',
        'showTrucks',
        'selectedShift',
        'hiddenScheduled',
        'scheduled',
        'filtered',
      ])
    ) {
      await this.loadAllFeatures();

      if (someObjectValuesChanged(this.props, prevProps, ['filtered'])) {
        await this.displayStats(this.state.fieldBoundaries as Feature<Geometry>[]);
      }
    }
    // if (JSON.stringify(this.props.filtered) !== JSON.stringify(prevProps.filtered)) {
    //     await this.loadAllFeatures();
    // }
    // else if (this.props.selectedShift !== prevProps.selectedShift) {
    //     await this.loadAllFeatures();
    // }
    // else if (this.props.showJobs !== prevProps.showJobs) {
    //     await this.loadAllFeatures();
    // }
    // else if (this.props.showDropoffs !== prevProps.showDropoffs) {
    //     await this.loadAllFeatures();
    // }
    // else if (this.props.hiddenScheduled !== prevProps.hiddenScheduled) {
    //     await this.loadAllFeatures();
    // }
    // else if (this.props.scheduled !== prevProps.scheduled) {
    //     await this.loadAllFeatures();
    // }

    if (this.props.zoomField !== prevProps.zoomField && this.props.zoomField?.fields) {
      const zoomField = this.props.zoomField;
      const pullinCoords = this.getPullinFromZoomField(zoomField);

      if (pullinCoords) {
        dispatchSetCenter(pullinCoords);
        dispatchSetZoom(15);
      }
    }
  }

  updateSelectedFeatures(selectedFeatures: Collection<Feature>) {
    this.setState({ selectedFeatures });
  }

  getPullinFromZoomField(zoomField: LocalAtRecord) {
    let pullinCoords: Coordinate | undefined = undefined;

    if (zoomField.table === 'Jobs') {
      const pullinCoordsString = zoomField.fields['Location']
        ? (zoomField.fields['Location'] as string).split(',')
        : undefined;

      if (pullinCoordsString) {
        pullinCoords = convertProjection4329([parseFloat(pullinCoordsString[1]), parseFloat(pullinCoordsString[0])]);
      }
    } else {
      if (zoomField.fields['Dropoff Lat'] && zoomField.fields['Dropoff Lon']) {
        const lon = zoomField.fields['Dropoff Lon'];
        const lat = zoomField.fields['Dropoff Lat'];
        pullinCoords = convertProjection4329([Array.isArray(lon) ? lon[0] : lon, Array.isArray(lat) ? lat[0] : lat]);
      }
    }

    if (!pullinCoords) {
      alertWarn('No valid pullin coordinates found to zoom to!');
    }

    return pullinCoords;
  }

  getPullinFromJob(job: LocalAtRecord): [number, number] | undefined {
    let pullinCoords: Coordinate | undefined = undefined;

    if (job.table === 'Jobs') {
      const pullinCoordsStrings = job.get('Location') ? (job.get('Location') as string).split(',') : undefined;

      if (pullinCoordsStrings) {
        pullinCoords = convertProjection4329([parseFloat(pullinCoordsStrings[1]), parseFloat(pullinCoordsStrings[0])]);
      }
    } else {
      if (job.get('Dropoff Lat') && job.get('Dropoff Lon')) {
        const lon = job.get('Dropoff Lon');
        const lat = job.get('Dropoff Lat');
        pullinCoords = convertProjection4329([Array.isArray(lon) ? lon[0] : lon, Array.isArray(lat) ? lat[0] : lat]);
      }
    }

    return pullinCoords as [number, number] | undefined;
  }

  loadUnassignedFeatures(unassigned: LocalAtRecord[], boundaries: JobBoundsMap) {
    const unassignedFeatures = unassigned.flatMap((job) => {
      // Iterate through unassigned jobs and add the features
      const baseProps = this.getBaseProps(job);
      baseProps.unassigned = true;
      const pullinCoords = this.getPullinFromJob(job);
      const collectedFeats = this.collectScheduleFeatures(boundaries, baseProps, job, pullinCoords); // Generate features from the boundaries

      return collectedFeats;
    });

    return unassignedFeatures;
  }

  loadOrderLine(pullinCoords, lastJob, robot) {
    const orderLineFeature: KMLFeature = {
      type: 'Feature',
      properties: { type: 'OrderLine', robot },
      geometry: { type: GeometryType.LINE_STRING, coordinates: [lastJob, pullinCoords] },
    };

    return orderLineFeature;
  }

  loadScheduledFeatures(scheduled: ScheduleMap, boundaries: JobBoundsMap) {
    const scheduledFeatureList = Object.keys(scheduled).flatMap((key) => {
      const scheduleValues = key.split('/');
      let lastJob: Coordinate | undefined = undefined;
      let first = false;

      return scheduled[key].flatMap((job) => {
        let jobFeatures: KMLFeature[] = []; // features for this specific job
        const baseProps = this.getBaseProps(job);
        baseProps.unassigned = false;
        /* using the key for these values is faster than the getter.
                Over a large amount of jobs it makes a difference */
        baseProps.schedDate = scheduleValues[0];
        baseProps.robot = scheduleValues[1];
        baseProps.operatorName = scheduleValues[2];
        baseProps.rig = scheduleValues[3];

        // @ts-ignore TODO: fix this
        baseProps.scheduleOrder = job.get('Order');

        /* this is used for determining what job is first.
                We don't use order because the first item in the order is not always equal to 0 */
        if (!first) {
          baseProps.first = true;
          first = true;
        }

        baseProps.selected = key === this.props.selectedShift;
        const pullinCoords = this.getPullinFromJob(job);

        if (key === this.props.selectedShift && lastJob && pullinCoords && pullinCoords.length) {
          jobFeatures.push(this.loadOrderLine(pullinCoords, lastJob, scheduleValues[0]));
          lastJob = pullinCoords;
        } else if (key === this.props.selectedShift && pullinCoords && pullinCoords.length) {
          lastJob = pullinCoords;
        }

        const collectedFeats = this.collectScheduleFeatures(boundaries, baseProps, job, pullinCoords);
        jobFeatures = jobFeatures.concat(collectedFeats);

        return jobFeatures;
      });
    });

    return scheduledFeatureList;
  }

  collectScheduleFeatures(
    boundaries: JobBoundsMap,
    baseProps: SchedulingBaseProps,
    job: LocalAtRecord,
    pullinCoords?: Coordinate,
  ) {
    if (!pullinCoords) {
      return [];
    }

    const featureList: KMLFeature[] = [];
    const selectedFields = this.getJobArray();
    const bounds = boundaries[job.id];
    const internalOrder = selectedFields.findIndex((selectedJob) => selectedJob && selectedJob.id === job.id);

    if (bounds) {
      bounds.forEach((bound) => {
        const coords = bound.coords;
        const polyType = bound.type as GeometryType;
        const polyProps = Object.assign({}, baseProps);
        polyProps.type = 'Field';
        polyProps.internalOrder = internalOrder;
        const polygon = {
          type: 'Feature',
          properties: polyProps,
          geometry: {
            type: polyType,
            coordinates: coords,
          },
        };
        featureList.push(polygon);
      });
    }

    const pullinProps = Object.assign({}, baseProps);
    pullinProps.type = 'Pullin';
    pullinProps.internalOrder = internalOrder;
    const pullin = {
      type: 'Feature',
      properties: pullinProps,
      geometry: {
        type: GeometryType.POINT,
        coordinates: pullinCoords,
      },
    };
    featureList.push(pullin);

    return featureList;
  }

  async loadAllFeatures() {
    const boundaries = this.props.jobBounds; // the parsed boundaries
    // only want to show filtered jobs
    const unassigned = this.props.filtered;
    const scheduled = { ...this.props.scheduled };
    Object.keys(scheduled).forEach((key) => {
      if (this.props.hiddenScheduled.includes(key)) {
        scheduled[key] = scheduled[key] = [];
      }
    });
    let finalList: KMLFeature<DefaultKMLFeatureProperties>[] = []; // list of all the features to be added
    const scheduledFeatures = this.loadScheduledFeatures(scheduled, boundaries);
    finalList = finalList.concat(scheduledFeatures);
    const unassignedFeatures = this.loadUnassignedFeatures(unassigned, boundaries);
    finalList = finalList.concat(unassignedFeatures);

    this.setState({ scheduleFeatures: finalList });
  }

  findBlockerJobs(jobs: LocalAtRecord<JobsFieldSet>[]) {
    const blockerJobIds: Set<string> = new Set();

    for (const job of jobs) {
      const jobFlags = job.get('Job Flags') || [];

      // @ts-ignore
      const jobOnlyNeedsCall = jobFlags.includes('Call Prior To Schedule');
      const dealOrClientSaysJobNeedsCall = job.get('Call Prior To Schedule? (From Client+Deal)') || false;
      const jobNeedsCall = jobOnlyNeedsCall || dealOrClientSaysJobNeedsCall;

      // @ts-ignore
      const jobAlreadyCalled = jobFlags.includes('Customer called');

      if (jobNeedsCall && !jobAlreadyCalled) {
        blockerJobIds.add(job.id);
        alertInfo(`Job ${job.get('Name')} for ${job.get('Client')} needs to be called before it can be scheduled.`);
      }

      // Check ops notes reviewed logic

      // Temporarily - do not check the notes.
      // const jobNotesReviewed = job.get('Job Notes Reviewed') || false;
      // const hasNotesToReview = Job.jobHasNotesThatNeedReview(job);
      // if (hasNotesToReview && jobNotesReviewed === false) {
      //   blockerJobIds.add(job.id);
      //   alertInfo(`Job ${job.get('Name')} for ${job.get('Client')} needs job notes reviewed.`);
      // }
    }

    return jobs.filter((job) => blockerJobIds.has(job.id));
  }

  prepareOrderedRecords(recordsOnShift: LocalAtRecord[], selectedFields: SchedulingRecordTypes[]) {
    let orderedRecords: LocalAtRecord[] = recordsOnShift || [];
    const allShiftTargetsIds = orderedRecords.map((record) => record.id);

    const selectedInShift = selectedFields.filter((job) => job && allShiftTargetsIds.includes(job.id)); // grab all jobs that are selected that are also in the shift

    const shiftJobsSelected = selectedFields.map((job) => job?.id);

    if (selectedInShift.length === 0) {
      orderedRecords = orderedRecords.concat(selectedFields); // Append to end of shift then return
    } else {
      const anchor = selectedInShift[0]; // anchor will determine order to migrate internal order to job order
      orderedRecords = orderedRecords.filter(
        (record) => !shiftJobsSelected.includes(record.id) || anchor.id === record.id,
      ); // remove all jobs except anchor
      const anchorIdx = orderedRecords.findIndex((record) => record.id === anchor.id); // get index of anchor
      orderedRecords = orderedRecords.filter((record) => record.id !== anchor.id); // now filter out the anchor

      for (const job of selectedFields.reverse()) {
        // Insert all of the elements with their internal order
        orderedRecords.splice(anchorIdx, 0, job);
      }
    }

    console.log('ORDERED RECORDS', orderedRecords.map((job) => job.id).join(','));

    return orderedRecords;
  }

  async refreshJobRecords(jobs: LocalAtRecord<JobsFieldSet>[]): Promise<Record<string, LocalAtRecord<JobsFieldSet>>> {
    const filterFormula = `OR(${jobs.map((job) => `{Rogo Job ID} = "${job.id}"`).join(',')})`;
    const search = await getLocalAtSearch<JobsFieldSet>('Jobs', '', filterFormula, false);
    await search.refresh({ waitForAttachments: false });

    const refreshedJobsByIds: Record<string, LocalAtRecord<JobsFieldSet>> = search.getRecords().reduce(
      (acc, job) => {
        acc[job.id] = job;

        return acc;
      },
      {} as Record<string, LocalAtRecord<JobsFieldSet>>,
    );

    return refreshedJobsByIds;
  }

  async refreshAndValidateRecordsToBeScheduled(
    recordsOnShift: LocalAtRecord<any>[],
    selectedFields: SchedulingRecordTypes[],
  ): Promise<[boolean, LocalAtRecord<any>[], SchedulingRecordTypes[]]> {
    const jobRecordsToRefresh: LocalAtRecord<JobsFieldSet>[] = dedupeBy(
      recordsOnShift
        .filter((record) => record.table === 'Jobs')
        .concat(selectedFields.filter((record) => record.table === 'Jobs')),
      'id',
    );

    // Refresh jobs that are going to be scheduled.
    const refreshedJobsById: Record<string, LocalAtRecord<JobsFieldSet>> = await this.refreshJobRecords(
      jobRecordsToRefresh,
    );

    if (jobRecordsToRefresh.length !== Object.keys(refreshedJobsById).length) {
      alertError('Cannot schedule - failed to refresh the jobs to be scheduled, some jobs must be missing.');

      return [false, [], []];
    }

    const outRecordsOnShift = recordsOnShift.map((record) => refreshedJobsById[record.id] || record);
    const outSelectedFields = selectedFields.map((record) => refreshedJobsById[record.id] || record);

    // Check if any jobs on shift or selected fields have already been sampled.
    const alreadySampledOnShift: LocalAtRecord<JobsFieldSet>[] = outRecordsOnShift.filter(
      (record) => record.table === 'Jobs' && (record as LocalAtRecord<JobsFieldSet>).get('Sample Date'),
    );

    const alreadySampledSelected: LocalAtRecord<JobsFieldSet>[] = outSelectedFields.filter(
      (record) => record.table === 'Jobs' && (record as LocalAtRecord<JobsFieldSet>).get('Sample Date'),
    ) as LocalAtRecord<JobsFieldSet>[];

    if (alreadySampledOnShift.length > 0 || alreadySampledSelected.length > 0) {
      alertError(
        `Cannot schedule. The following jobs have already been sampled: ${alreadySampledOnShift
          .concat(alreadySampledSelected)
          .map((job) => job.get('Name'))
          .join(', ')}`,
      );

      return [false, [], []];
    }

    return [true, outRecordsOnShift, outSelectedFields];
  }

  // 1. Get current shift (selected from sidebar)
  // 2. Get currently selected jobs (and dropoffs?)
  // 3. Get the current "Schedule Map"
  // 4.

  async assignShift() {
    this.props.updateLoading(true);
    const shift = getCurrentShift(this.props.selectedShift, this.props.availableShifts);
    let selectedFields = this.getJobArray();
    const scheduled = this.props.scheduled;
    console.log(`Shift: ${shift?.fields['Operator Name']}`);
    console.log(selectedFields.map((field) => field?.fields['Name']).join(','));

    if (shift) {
      const shiftRobot = shift.get('Robot')?.[0];
      const shiftSchedDate = shift.get('Sched Date');
      const shiftOperatorName = shift.get('Operator Name');
      const shiftRig = shift.get('Rig #') || '';

      if (!shiftRobot || !shiftSchedDate || !shiftOperatorName || !shiftRig) {
        alertWarn('Selected shift is missing required fields (Robot, Sched Date, Operator Name, Rig #)');
        this.props.updateLoading(false);

        return;
      }

      const key = generateScheduledKey(shiftSchedDate, shiftRobot, shiftOperatorName, shiftRig);
      let recordsOnShift: LocalAtRecord[] = scheduled[key] || [];
      console.log('records on shift', recordsOnShift);

      let recordsValidationSuccess: boolean = false;
      [recordsValidationSuccess, recordsOnShift, selectedFields] = await this.refreshAndValidateRecordsToBeScheduled(
        recordsOnShift,
        selectedFields,
      );

      if (!recordsValidationSuccess) {
        this.props.updateLoading(false);

        return;
      }

      const orderedRecords: LocalAtRecord[] = this.prepareOrderedRecords(recordsOnShift, selectedFields);

      // Check blocker jobs.
      const blockerJobs = this.findBlockerJobs(
        orderedRecords.filter((record) => record.table === 'Jobs') as LocalAtRecord<JobsFieldSet>[],
      );

      if (blockerJobs.length > 0) {
        // skip scheduling, put global alert
        alertError(`Cannot schedule. There are jobs that have not met the scheduling requirements.`);
        this.props.updateLoading(false);

        return;
      }

      // Done with re-order... Now time to upload this to airtable and adjust other scheduled jobs and unassigned jobs
      await this.updateRecordsInAirtable(orderedRecords, key);
    } else {
      // Assigning to unscheduled
      const onlyScheduled = selectedFields.filter((job) =>
        (['Shift Dropoffs', 'Dropoffs'].includes(job.table) && job.get('Shift')) || job.get('Scheduled?')
          ? // @ts-ignore
            !!job.get('Shift') && job.get('Shift')!.length // @ts-ignore
          : false,
      ); // avoid duplication

      await this.updateRecordsInAirtable(onlyScheduled, '');
    }

    this.props.updateLoading(false);
  }

  getBaseProps(job: LocalAtRecord, fillColorsByTestPackages: Record<string, string> = {}): SchedulingBaseProps {
    if (job.table === 'Jobs') {
      return {
        job_id: job.id,
        field_name: job.get('Field Name Clean'),
        lab: job.get('Lab Shortname') ? job.get('Lab Shortname')[0] : undefined,
        client: job.get('Client') ? job.get('Client')[0] : undefined,
        grower: job.get('Grower') ? job.get('Grower')[0] : undefined,
        pullinType: job.table,
        fillColor: fillColorsByTestPackages[job.get('Test Pckg - Final')] || '',
      };
    } else {
      return {
        job_id: job.id,
        pullinType: job.table,
        dropoff_name:
          job.table === 'Dropoffs' ? job.get('Name') : job.get('Dropoff Name') ? job.get('Dropoff Name')[0] : '',
        dropoff_lab:
          job.table === 'Dropoffs' ? job.get('Labs') : job.get('Dropoff Lab') ? job.get('Dropoff Lab')[0] : '',
        address:
          job.table === 'Dropoffs'
            ? job.get('Location')
            : job.get('Dropoff Location')
              ? job.get('Dropoff Location')[0]
              : '',
      };
    }
  }

  checkChanged(job: LocalAtRecord, shiftId: string[], currentOrder: number) {
    // got a sentry error about not being able to access get on undefined. Job must be
    // undefined but not sure how? Still will do a check and add logging to be safe
    if (!job) {
      logger.log('SCHEDULING CHECK_CHANGED', 'Job is undefined');

      return false;
    }

    const jobShiftId = job.get('Shift') || [];
    const order = job.get('Order');

    return JSON.stringify(jobShiftId) !== JSON.stringify(shiftId) || order !== currentOrder;
  }

  getJobArray() {
    const selectedFeatures = this.state.selectedFeatures
      .getArray()
      .slice()
      .map((feature) => getJobFromFeature(this.props.scheduled, this.props.unassigned, feature));

    return selectedFeatures.filter((sel) => !!sel);
  }

  async modifyJob(
    record: LocalAtRecord,
    shift: LocalAtRecord | undefined,
    currentOrder: number,
    jobsOfSingleDropoff: LocalAtRecord<JobsFieldSet>[],
    { immediateSync = true } = {},
  ) {
    return await new Promise<LocalAtRecord<any>>(async (resolve, reject) => {
      const shiftId = shift ? [shift.id] : [];
      currentOrder = shift ? currentOrder : 0;
      const scheduleItemHasChanged = this.checkChanged(record, shiftId, currentOrder);

      if (record.table === 'Jobs') {
        if (scheduleItemHasChanged) {
          await record.set('Shift', shiftId);
          await record.set('Order', currentOrder);

          if (shiftId.length > 0 && record.get('Skipped by Operator Time')) {
            // if scheduling on a shift, clear out the last skipped time
            await record.set('Skipped by Operator Time', null);
          }

          if (immediateSync && record.dirty) {
            await record.sync();
          }
        }
      } else if (record.table === 'Shift Dropoffs') {
        if (scheduleItemHasChanged) {
          await record.set('Shift', shiftId);
          await record.set('Order', currentOrder);

          if (immediateSync && record.dirty) {
            await record.sync();
          }
        }
      } else if (record.table === 'Dropoffs') {
        const newShiftDropoff = new ShiftDropoff({
          jobs: jobsOfSingleDropoff.map((j) => j.id),
          dropoff: record.id,
          shift: shiftId.at(0),
          order: currentOrder,
        });
        await newShiftDropoff.save();

        // Before refactor: Airtable.createRecord (which called .base.create and AirtableRecordClass.createRecord)
        record = createLocalAtRecordByAtModel(newShiftDropoff);
      } else {
        reject(`Unsupported table ${record.table}`);
      }

      record = LocalAtRecord.getRecord(record.table, record.id)!;
      resolve(record);
    });
  }

  async scheduleRecord(
    job: LocalAtRecord,
    currentOrder: number,
    jobsOfSingleDropoff: LocalAtRecord<JobsFieldSet>[],
    dirtyUpdates: LocalAtRecord[],
    shift: LocalAtRecord | undefined,
    unassigned: LocalAtRecord[],
  ): Promise<SchedulingRecordTypes | undefined> {
    let updatedRecord: SchedulingRecordTypes | undefined;
    console.log(`Job ${job.fields['Name']} is not scheduled`);

    const idx = unassigned.findIndex((unassignedJob) => unassignedJob.id === job.id);

    if (idx === -1) {
      Sentry.setContext('assign_shift', {
        job: job?.id,
        unassigned: JSON.stringify(unassigned),
      });

      alertError(`Could not find job ${job?.id} in unassigned jobs`);
      Sentry.captureMessage('Could not find job in unassigned jobs');

      return;
    }

    updatedRecord = unassigned[idx];

    if (updatedRecord) {
      updatedRecord = await this.modifyJob(updatedRecord, shift, currentOrder, jobsOfSingleDropoff, {
        immediateSync: false,
      }); // add the correct properties
    }

    if (updatedRecord && updatedRecord.dirty) {
      dirtyUpdates.push(updatedRecord);
    }

    if (job.table === 'Jobs') {
      // Only remove job from unassigned
      unassigned.splice(idx, 1);
    }

    return updatedRecord;
  }

  async unscheduleOrRescheduleRecord(
    job: LocalAtRecord,
    currentOrder: number,
    jobsOfSingleDropoff: LocalAtRecord<JobsFieldSet>[],
    dirtyUpdates: LocalAtRecord[],
    newShiftRecord: LocalAtRecord | undefined,
    scheduled: ScheduleMap,
  ): Promise<SchedulingRecordTypes | undefined> {
    let updatedRecord: SchedulingRecordTypes | undefined;

    console.log(`Job ${job.fields['Name']} is scheduled`);

    const jobShiftId = (job.get('Shift') || [])[0];
    const currentShiftRecord = await getShiftRecordById(jobShiftId);

    if (!currentShiftRecord) {
      alertError(`Could not find shift ${jobShiftId}`);

      return;
    }

    const schedDate = currentShiftRecord.get('Sched Date');

    if (!schedDate) {
      alertError(`Could not find sched date for shift ${jobShiftId}`);

      return;
    }

    const robot = currentShiftRecord.get('Robot')?.[0];

    if (!robot) {
      alertError(`Could not find robot for shift ${jobShiftId}`);

      return;
    }

    const operatorName = currentShiftRecord.get('Operator Name')?.[0];

    if (!operatorName) {
      alertError(`Could not find operator for shift ${jobShiftId}`);

      return;
    }

    const rig = currentShiftRecord.get('Rig #') || '';

    if (!rig) {
      alertError(`Could not find rig for shift ${jobShiftId}`);

      return;
    }

    const currentKey = generateScheduledKey(schedDate, robot, operatorName, rig);

    if (scheduled[currentKey]) {
      const idx = scheduled[currentKey]?.findIndex((scheduledJob) => scheduledJob.id === job.id);
      const idxFound = idx !== undefined && idx >= 0;

      updatedRecord = idxFound ? scheduled[currentKey][idx] : undefined;

      if (updatedRecord) {
        updatedRecord = await this.modifyJob(updatedRecord, newShiftRecord, currentOrder, jobsOfSingleDropoff, {
          immediateSync: false,
        });
      }

      if (updatedRecord && updatedRecord.dirty) {
        dirtyUpdates.push(updatedRecord);
      }

      if (idxFound) {
        scheduled[currentKey].splice(idx, 1);
      }
    }

    return updatedRecord;
  }

  async updateRecordsInAirtable(orderedRecordsToUpdate: LocalAtRecord[], key: string) {
    console.log('updateRecordsInAirtable - orderedRecordsToUpdate', orderedRecordsToUpdate, key);

    if (orderedRecordsToUpdate.length === 0 && !key) {
      this.setState({ selectedFeatures: new Collection([]) });

      return;
    }

    const scheduled: ScheduleMap = { ...this.props.scheduled };
    const scheduleJobIdMap: Record<string, string[]> = {};
    Object.keys(scheduled).forEach((key) => {
      scheduleJobIdMap[key] = scheduled[key].map((job) => job.id);
    });

    let unassigned = this.props.unassigned.slice();
    const assignToShift = getCurrentShift(this.props.selectedShift, this.props.availableShifts);

    const newShiftRecords: SchedulingRecordTypes[] = [];
    let jobsOfSingleDropoff: LocalAtRecord<JobsFieldSet>[] = [];
    const dirtyUpdates: LocalAtRecord[] = [];

    for (let currentOrder = 0; currentOrder < orderedRecordsToUpdate.length; currentOrder++) {
      const record = orderedRecordsToUpdate[currentOrder];
      let updatedRecord: SchedulingRecordTypes | undefined;

      const isScheduledJob = record.table === 'Jobs' && Boolean(record.get('Shift'));
      const isScheduledDropoff =
        record.table === 'Shift Dropoffs' && record.get('Shift') ? (record.get('Shift') as any[]).length : 0; // use shift length as bool
      const isScheduled = isScheduledJob || isScheduledDropoff;

      if (!isScheduled) {
        updatedRecord = await this.scheduleRecord(
          record,
          currentOrder,
          jobsOfSingleDropoff,
          dirtyUpdates,
          assignToShift,
          unassigned,
        );
      } else {
        updatedRecord = await this.unscheduleOrRescheduleRecord(
          record,
          currentOrder,
          jobsOfSingleDropoff,
          dirtyUpdates,
          assignToShift,
          scheduled,
        );
      }

      if (assignToShift) {
        if (['Dropoffs', 'Shift Dropoffs'].includes(record.table) && updatedRecord) {
          // If the current record is a dropoff, update previously added jobs with this dropoff.
          for (const jobRecord of jobsOfSingleDropoff) {
            const dropoff = await this.updateJobDropoff(jobRecord, [updatedRecord.id], { immediateSync: false });

            if (dropoff.dirty) {
              dirtyUpdates.push(dropoff);
            }
          }

          jobsOfSingleDropoff = [];
        } else if (record.table === 'Jobs') {
          // If the current record is a job, add it to the group of jobs for this dropoff.
          jobsOfSingleDropoff.push(record);
        }
      }

      if (updatedRecord) {
        newShiftRecords.push(updatedRecord);
      }
    } // for...

    if (assignToShift) {
      scheduled[key] = newShiftRecords;
      /* Need to directly modify these links, we are not refreshing the shift for efficiency */
      assignToShift.fields['Jobs link'] = newShiftRecords.filter((job) => job.table === 'Jobs').map((job) => job.id);
      assignToShift.fields['Shift Dropoffs'] = newShiftRecords
        .filter((job) => job.table === 'Shift Dropoffs')
        .map((job) => job.id);
    } else {
      unassigned = unassigned.concat(newShiftRecords.filter((job) => job.table === 'Jobs'));
    }

    Sentry.setContext('assign_shift', {
      recordIds: dirtyUpdates.map((record) => record.id),
      operator: assignToShift ? assignToShift.get('Operator Name') : 'Unassigned',
    });

    await syncMulti(dirtyUpdates);
    EventBus.dispatch('SCHEDULING:REFRESH_JOBS');

    this.props.updateScheduled(scheduled);
    this.props.updateUnassigned(unassigned);

    this.setState({ selectedFeatures: new Collection([]) });
  }

  async updateJobDropoff(job: LocalAtRecord, dropoff: string[], { immediateSync = true } = {}) {
    return await new Promise<LocalAtRecord>(async (resolve, reject) => {
      const currentDropoff = job.get('Dropoffs/Shipping') || [];

      if (JSON.stringify(currentDropoff) !== JSON.stringify(dropoff)) {
        await job.set('Dropoffs/Shipping', dropoff);

        if (immediateSync && job.dirty) {
          await job.sync();
        }
      }

      resolve(job);
    });
  }

  toggleStatsDraw = () => {
    this.setState({ statsDrawActive: !this.state.statsDrawActive, showStats: false });
  };

  toggleHeatmap = () => {
    this.setState({ showHeatmap: !this.state.showHeatmap });
  };

  setHeatmapAttribute = (attribute: HeatmapAttribute) => {
    this.setState({ heatmapAttribute: attribute });
  };

  setHeatmapBlur = (blur: number) => {
    this.setState({ heatmapBlur: blur });
  };

  setHeatmapRadius = (radius: number) => {
    this.setState({ heatmapRadius: radius });
  };

  //async displayStats(fieldBoundaries: Feature<Polygon>[]) {
  displayStats = async (
    fieldBoundaries: Feature<Geometry>[],
    groupByFieldName: GroupStatisticsBy = 'Test Pckg - Final',
  ) => {
    let totalAcres = 0;
    let totalOpsAcres = 0;
    let totalSamples = 0;
    let numValidAcres = 0;
    let numValidSamples = 0;
    let totalFields = 0;
    const jobLinks: { name: string; link: string }[] = [];
    const baseLink = `https://airtable.com/${import.meta.env.VITE_AIRTABLE_BASE_ID}/tblXij9xpNwUCLRIM/viwzbSmFpjFlulGg8`;

    const fieldBoundariesFiltered = fieldBoundaries.filter((fieldBoundary) => {
      // @ts-ignore
      return fieldBoundary.properties.job_id && fieldBoundary.properties.type === 'Field';
    });

    const groupByInfo: StatisticsGrouped = {};
    const existIds: string[] = [];

    for (const fieldBoundary of fieldBoundariesFiltered) {
      // @ts-ignore
      const jobId = fieldBoundary.properties.job_id as string;

      if (existIds.includes(jobId)) {
        continue;
      }

      const job = LocalAtRecord.getRecord<JobsFieldSet>('Jobs', jobId);

      if (!job) {
        continue;
      }

      const jobLink = {
        name: job.get('Field from Form') || 'No Field Name',
        link: `${baseLink}/${jobId}`,
      };

      if (!jobLinks.find((jl) => jl.link === jobLink.link)) {
        jobLinks.push(jobLink);
      }

      const acres = parseFloat(job.get('Boundary Acres')?.toString() || '0');

      const opsAcres = job.get('Ops Acre Eq.');

      if (opsAcres && typeof opsAcres === 'number') {
        totalOpsAcres += opsAcres;
      }

      const samples = parseInt(job.get('# Samples')?.toString() || '0');
      totalAcres += acres;
      totalSamples += samples;

      if (acres) {
        numValidAcres++;
      }

      if (samples) {
        numValidSamples++;
      }

      if (groupByFieldName) {
        const groupByFieldValue = job.get(groupByFieldName)?.toString();

        if (groupByFieldValue) {
          if (!groupByInfo[groupByFieldValue]) {
            groupByInfo[groupByFieldValue] = {
              totalFields: 0,
              totalAcres: 0,
              totalSamples: 0,
              fieldsWithValidAcresNumber: 0,
              fieldsWithValidSamplesNumber: 0,
            };
          }

          groupByInfo[groupByFieldValue].totalFields++;
          groupByInfo[groupByFieldValue].totalAcres += acres;
          groupByInfo[groupByFieldValue].totalSamples += samples;

          if (acres) {
            groupByInfo[groupByFieldValue].fieldsWithValidAcresNumber++;
          }

          if (samples) {
            groupByInfo[groupByFieldValue].fieldsWithValidSamplesNumber++;
          }
        }
      }

      totalFields++;
      existIds.push(jobId);
    }

    totalAcres = Math.round(totalAcres);
    totalOpsAcres = Math.round(totalOpsAcres);
    totalSamples = Math.round(totalSamples);

    const averageSamples = numValidSamples > 0 ? Math.round(totalSamples / numValidSamples) || 0 : 0;
    const averageAcres = numValidAcres > 0 ? Math.round(totalAcres / numValidAcres) || 0 : 0;

    this.setState({
      showStats: totalFields > 0,
      totalAcres,
      totalOpsAcres,
      totalSamples,
      averageSamples,
      averageAcres,
      totalFields,
      jobLinks,
      fieldBoundaries,
      statisticsGrouped: groupByInfo,
    });
  };

  setWeatherDays = async (weatherDays: number) => {
    const weatherDayPromises: Promise<void>[] = [];

    for (const fieldBoundary of this.state.fieldBoundaries) {
      // @ts-ignore
      const job = LocalAtRecord.getRecord<JobsFieldSet>('Jobs', fieldBoundary.properties.job_id);

      if (!job) {
        continue;
      }

      const currentWeatherDays = job.get('Weather Days (Customer Approved)') || 0;
      await job.set('Weather Days (Customer Approved)', currentWeatherDays + weatherDays);
      const syncPromise = job.sync();
      weatherDayPromises.push(syncPromise);
    }

    await Promise.all(weatherDayPromises);
  };

  deselectAll() {
    this.setState({ selectedFeatures: new Collection([]) });
  }

  renderLayers() {
    const out = [
      <SchedulingLayer
        key="scheduling-layer"
        // @ts-ignore
        scheduleFeatures={this.state.scheduleFeatures}
        updateSelectedFeatures={this.updateSelectedFeatures}
        selectedFeatures={this.state.selectedFeatures}
        displayStats={this.displayStats}
        statsDrawActive={this.state.statsDrawActive}
      />,
    ];

    if (this.props.showRobots) {
      out.push(<LiveRobotLayer key="live-robot-layer" allRobots={this.props.allRobots} />);
    }

    if (this.props.showTrucks) {
      out.push(<LiveTruckLayer key="live-truck-layer" allTrucks={this.props.allTrucks} />);
    }

    if (this.state.showHeatmap) {
      out.push(
        <HeatStatsLayer
          key="heatmap-layer"
          jobs={this.props.filtered.filter((record) => record.table === 'Jobs') as LocalAtRecord<JobsFieldSet>[]}
          attribute={this.state.heatmapAttribute}
          blur={this.state.heatmapBlur}
          radius={this.state.heatmapRadius}
          gradient={this.state.heatmapGradient}
          getPullinFromJob={this.getPullinFromJob.bind(this)}
        />,
      );
    }

    return out;
  }

  render() {
    return (
      <BaseMap
        buttons={[
          <MapButton
            key="stats-button"
            onClick={this.toggleStatsDraw}
            toggled={this.state.statsDrawActive}
            tooltip="Show stats"
          >
            <IoIosStats size={20} />
          </MapButton>,
          <MapButton
            key="heatmap-button"
            onClick={this.toggleHeatmap}
            toggled={this.state.showHeatmap}
            tooltip="Toggle heatmap"
          >
            <MdShowChart size={20} />
          </MapButton>,
          this.state.showHeatmap && (
            <div
              key="heatmap-controls"
              style={{
                position: 'absolute',
                top: '60px',
                right: '10px',
                zIndex: 1000,
                backgroundColor: 'white',
                padding: '10px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              <FormControl size="small" style={{ minWidth: 150 }}>
                <InputLabel>Heatmap Attribute</InputLabel>
                <Select
                  value={this.state.heatmapAttribute}
                  onChange={(e) => this.setHeatmapAttribute(e.target.value as HeatmapAttribute)}
                  label="Heatmap Attribute"
                >
                  {HeatmapAttributeCalculator.getAllAttributes().map((attr) => {
                    const config = HeatmapAttributeCalculator.getConfig(attr);

                    return (
                      <MenuItem key={attr} value={attr}>
                        {config.label}
                      </MenuItem>
                    );
                  })}
                </Select>

                <Grid container justifyContent="center" style={{ padding: 10 }} size={12}>
                  <Typography gutterBottom>Blur</Typography>

                  <Slider
                    style={{ width: '80%' }}
                    step={5}
                    value={this.state.heatmapBlur}
                    valueLabelDisplay="auto"
                    min={0}
                    max={100}
                    color="secondary"
                    onChange={debounce((e, value) => {
                      this.setHeatmapBlur(value);
                    }, 10)}
                  />
                </Grid>

                <Grid container justifyContent="center" style={{ padding: 10 }} size={12}>
                  <Typography gutterBottom>Radius</Typography>

                  <Slider
                    style={{ width: '80%' }}
                    step={5}
                    value={this.state.heatmapRadius}
                    valueLabelDisplay="auto"
                    min={0}
                    max={100}
                    color="secondary"
                    onChange={debounce((e, value) => {
                      this.setHeatmapRadius(value);
                    }, 10)}
                  />
                </Grid>
              </FormControl>

              <HeatmapHistogram
                jobs={this.props.filtered.filter((record) => record.table === 'Jobs') as LocalAtRecord<JobsFieldSet>[]}
                attribute={this.state.heatmapAttribute}
                gradient={this.state.heatmapGradient}
                getPullinFromJob={this.getPullinFromJob.bind(this)}
              />
            </div>
          ),
          this.state.selectedFeatures.getArray().length > 0 && (
            <AssignmentButton key="assignment-button" onClick={this.assignShift} />
          ),
          this.state.selectedFeatures.getArray().length > 0 && (
            <DeselectAllButton key="deselect-button" onClick={this.deselectAll} />
          ),
          this.state.showStats && (
            <SideBarInfoFields
              key="sidebar-info-fields"
              statisticsGrouped={this.state.statisticsGrouped}
              totalAcres={this.state.totalAcres}
              totalOpsAcres={this.state.totalOpsAcres}
              totalSamples={this.state.totalSamples}
              averageAcres={this.state.averageAcres}
              averageSamples={this.state.averageSamples}
              totalFields={this.state.totalFields}
              jobLinks={this.state.jobLinks}
              setWeatherDays={this.setWeatherDays}
              onClose={this.toggleStatsDraw}
              onGroupStatisticsByChange={async (newGroupBy: GroupStatisticsBy) => {
                await this.displayStats(this.state.fieldBoundaries as Feature<Geometry>[], newGroupBy);
              }}
            />
          ),
          // <PlaybackControls></PlaybackControls>,

          // <LayerCheckContainer>
          //     <LayerCustomCheckBox
          //         label={'Weather layer'}
          //         onChange={(e) => {
          //             this.setState({ showWeatherTiles: e.target.checked });
          //         }}
          //         checked={this.state.showWeatherTiles}
          //     />

          // </LayerCheckContainer>
        ]}
        layers={this.renderLayers()}
      />
    );
  }
}
